import useAuthenticatedFetch from "@/hooks/useAuthenticatedFetch.ts";
import { apiUrl } from "@/utils/api.ts";
import { type UseMutationResult, useMutation, useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";

type ExportDocumentsParams = {
    caseIdUUID: string;
};

type ExportDocumentsAsyncResponse = {
    jobId: string;
    message: string;
};

type ProgressResponse = {
    progress: number;
    status:
        | "started"
        | "fetching_data"
        | "data_fetched"
        | "preparing_download"
        | "completed"
        | "error"
        | "not_found";
    error?: string;
    downloadId?: string;
};

export const useStartDocumentExport = (): UseMutationResult<
    ExportDocumentsAsyncResponse,
    Error,
    ExportDocumentsParams
> => {
    const fetch = useAuthenticatedFetch();

    return useMutation({
        mutationFn: async (params: ExportDocumentsParams) => {
            const response = await fetch(apiUrl("/media/export-for-case"), {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(params),
            });

            if (!response.ok) {
                throw new Error(`Failed to start document export: ${response.statusText}`);
            }

            return response.json();
        },
    });
};

export const useTrackDocumentProgress = (jobId: string | null) => {
    const fetch = useAuthenticatedFetch();
    const [progress, setProgress] = useState(0);
    const [status, setStatus] = useState<ProgressResponse["status"] | null>(null);
    const [error, setError] = useState<Error | null>(null);
    const [downloadId, setDownloadId] = useState<string | null>(null);

    const { data, error: queryError } = useQuery({
        queryKey: ["document-progress", jobId],
        queryFn: async (): Promise<ProgressResponse> => {
            if (!jobId) {
                throw new Error("No job ID provided");
            }

            const response = await fetch(apiUrl(`/media/export-progress/${jobId}`));

            if (!response.ok) {
                throw new Error(`Failed to get progress: ${response.statusText}`);
            }

            return response.json();
        },
        enabled: !!jobId,
        refetchInterval: (query) => {
            // Stop polling if completed or errored
            const data = query.state.data;
            if (!data || data.status === "completed" || data.status === "error") {
                return false;
            }
            return 1000; // Poll every second
        },
        retry: (failureCount, error) => {
            // Don't retry if job not found
            if (error.message.includes("not_found")) {
                return false;
            }
            return failureCount < 3;
        },
        gcTime: 0, // Don't cache completed/errored queries
        staleTime: 0, // Always fetch fresh data
    });

    // Reset state when jobId changes (new download started)
    useEffect(() => {
        if (jobId) {
            setProgress(0);
            setStatus(null);
            setDownloadId(null);
            setError(null);
        }
    }, [jobId]);

    useEffect(() => {
        if (data) {
            setProgress(data.progress);
            setStatus(data.status);

            // Only set downloadId if it corresponds to the current jobId
            if (data.downloadId?.includes(jobId || "")) {
                setDownloadId(data.downloadId);
            } else if (!data.downloadId) {
                setDownloadId(null);
            }

            if (data.status === "error") {
                setError(new Error(data.error || "Unknown error occurred"));
            } else {
                setError(null);
            }
        }
    }, [data, jobId]);

    useEffect(() => {
        if (queryError) {
            setError(queryError as Error);
        }
    }, [queryError]);

    return {
        progress,
        status,
        error,
        downloadId,
        isPolling: !!jobId && status !== "completed" && status !== "error",
    };
};

// Combined hook for easier usage
export const useDownloadDocuments = () => {
    const startExportMutation = useStartDocumentExport();

    const startDownload = async (params: ExportDocumentsParams) => {
        try {
            return await startExportMutation.mutateAsync(params);
        } catch (error) {
            console.error("Failed to start document download:", error);
            throw error;
        }
    };

    return {
        startDownload,
        isLoading: startExportMutation.isPending,
        error: startExportMutation.error,
        isError: startExportMutation.isError,
        isSuccess: startExportMutation.isSuccess,
    };
};

export const useDownloadCompletedZip = () => {
    const fetch = useAuthenticatedFetch();

    return useMutation({
        mutationFn: async (downloadId: string): Promise<{ blob: Blob; filename: string }> => {
            const response = await fetch(apiUrl(`/media/download-zip/${downloadId}`));

            if (!response.ok) {
                throw new Error(`Failed to download ZIP: ${response.statusText}`);
            }

            const contentDisposition = response.headers.get("Content-Disposition");
            let filename = "case_documents.zip";

            if (contentDisposition) {
                const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);

                if (filenameMatch) {
                    filename = filenameMatch[1];
                }
            }

            return {
                blob: await response.blob(),
                filename,
            };
        },
    });
};
